import {
  LS_KEYS,
  MONTH_NAMES,
  REPORT_MODES,
  VISIBILITY_STATE
} from './my-working-report.constants';
import { ReportGroupViewModel, ReportSessionState, ReportViewModel } from './my-working-report.types';
import {ReportSelectionContext} from '../../services/report.service.types';
import {DEAL_PAYROLL_CALCULATION_REVISION, DEAL_PAYROLL_FORMULA_VERSION,
  isPlanfixDealPayroll} from '../../models/deal-payroll.model';

export class MyWorkingReportSession {
  invalidatedReports: ReportViewModel[] = [];
  reportsInvalidated = false;
  constructor(private storage: Storage, private isFreshReport: (raw: string) => boolean = () => false) {
  }

  public restore(): ReportSessionState {
    const visibilityState = this.restoreVisibility();
    const reports = this.readReports();
    return {
      visibilityState: visibilityState,
      isGroupMode: this.read(LS_KEYS.DRTS) === REPORT_MODES.GROUP,
      groupName: this.read(LS_KEYS.GROUP_NAME),
      groupId: this.read(LS_KEYS.GROUP_ID),
      group: this.readJson<ReportGroupViewModel>(LS_KEYS.DEP),
      reports: reports,
      reportDate: this.read(LS_KEYS.REPORT_DATE),
      reportDateFrom: this.read(LS_KEYS.REPORT_DATE_FROM),
      reportDateTo: this.read(LS_KEYS.REPORT_DATE_TO),
      currentMonth: MONTH_NAMES[new Date().getMonth()]
    };
  }

  public storeVisibility(visibilityState: string): void {
    this.storage.setItem(LS_KEYS.VISIBLE, visibilityState);
  }

  public readReports(): ReportViewModel[] {
    const raw = this.read(LS_KEYS.REPORTS);
    if (raw === null) { return this.resetReports([]); }
    try {
      const reports = JSON.parse(raw) as ReportViewModel[];
      if (!Array.isArray(reports)) { return this.resetReports([]); }
      if (reports.some(report => isPlanfixDealPayroll(report)) && !this.canReuseReports(raw, reports)) {
        return this.resetReports(reports);
      }
      this.reportsInvalidated = false;
      this.invalidatedReports = [];
      return reports;
    } catch (error) { return this.resetReports([]); }
  }

  public storeReports(reports: ReportViewModel[]): void {
    this.storage.setItem(LS_KEYS.REPORTS, JSON.stringify(reports));
    this.reportsInvalidated = false;
    this.invalidatedReports = [];
  }

  private canReuseReports(raw: string, reports: ReportViewModel[]): boolean {
    return this.isFreshReport(raw) && reports.every(report => !isPlanfixDealPayroll(report)
      || !!report.dealPayrollBreakdown
      && report.dealPayrollBreakdown.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION
      && report.dealPayrollBreakdown.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION);
  }

  private resetReports(reports: ReportViewModel[]): ReportViewModel[] {
    this.invalidatedReports = reports;
    this.reportsInvalidated = true;
    this.storage.removeItem(LS_KEYS.REPORTS);
    return [];
  }

  public reportDateFrom(): string {
    return this.read(LS_KEYS.REPORT_DATE_FROM);
  }

  public reportTarget(userId: string): string {
    return this.read(LS_KEYS.DRTS) === REPORT_MODES.GROUP
      ? JSON.stringify([REPORT_MODES.GROUP, this.read(LS_KEYS.GROUP_ID)]) : userId;
  }

  public reportContext(target: string): ReportSelectionContext {
    return {from: this.reportDateFrom(), to: this.reportDateTo(), target: target};
  }

  public reportDateTo(): string {
    return this.read(LS_KEYS.REPORT_DATE_TO);
  }

  private restoreVisibility(): string {
    const stored = this.read(LS_KEYS.VISIBLE);
    if (stored) {
      return stored;
    }
    this.storeVisibility(VISIBILITY_STATE.SHOW);
    return VISIBILITY_STATE.SHOW;
  }

  private read(key: string): string {
    return this.storage.getItem(key);
  }

  private readJson<T>(key: string): T {
    try { return <T>JSON.parse(this.read(key)); } catch (error) { return null; }
  }
}

export function createMyWorkingReportSession(isFreshReport?: (raw: string) => boolean): MyWorkingReportSession {
  return new MyWorkingReportSession(localStorage, isFreshReport);
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/my-working-report-session.ts